import React from "react";
import PropTypes from "prop-types";
import { Typography, Grid, Box, Button } from "@material-ui/core";
import classNames from "classnames";

import useStyles from "./styles";
import { ReactComponent as CameraIcon } from "../../images/icons/camera.svg";
import MovieAlbumItem from "../MovieAlbumItem";
import WideVideoAlbumItem from "../../components/WideVideoAlbumItem";

import get from "lodash/get";
import isEmpty from "lodash/isEmpty";
import { staticUrl } from "../../clientAPI";

const MovieAlbum = props => {
  const classes = useStyles();
  const {
    slot = {},
    hideTitle,
    hideTitleIcon,
    hideMoreLink,
    hoverMaskMode,
    hoverEnlargePoster,
    extraMarginBottom,
  } = props;
  const firstFourMovies = get(slot, "items", []).slice(0, 4);

  const DefaultLayout = () => {
    return (
      <>
        {slot.items.map((movie, index) => (
          <Grid item xs={4} md={2} key={`${movie.id}`}>
            <MovieAlbumItem
              key={`album-item-${movie.id}`}
              name={movie.name}
              item={movie}
              hoverMaskMode={hoverMaskMode}
              hoverEnlargePoster={hoverEnlargePoster}
              extraMarginBottom={extraMarginBottom}
            ></MovieAlbumItem>
          </Grid>
        ))}
      </>
    );
  };

  const ImgTopicLayout = () => {
    return (
      <>
        <Box className={classes.topicContainer}>
          <Box className={classes.topicPoster}>
            <a href={slot.img_url}>
              <img
                src={`${staticUrl}/${slot.img_loc}`}
                alt={slot.name}
                className={classNames(classes.imgFluid, classes.topicImg)}
              ></img>
            </a>
          </Box>
          {firstFourMovies.map(movie => (
            <Box className={classes.topicMovie} key={`album-item-${movie.id}`}>
              <MovieAlbumItem
                name={movie.name}
                item={movie}
                showMeta={false}
                showTitle={false}
              ></MovieAlbumItem>
            </Box>
          ))}
        </Box>
      </>
    );
  };

  const WidePosterLayout = () => (
    <>
      <Box className={classes.widePosterContainer}>
        <Box className={classes.widePosterChild}>
          <Box className={classes.largeWideContainer} mb={0.5}>
            <WideVideoAlbumItem
              size='lg'
              id={slot.items[0].id}
              poster={slot.items[0].poster}
              name={slot.items[0].name}
              title={slot.items[0].name}
              description={slot.items[0].description}
              others={slot.items[0].others}
            />
          </Box>
          <Box className={classes.smallWideContainer}>
            <Box className={classes.smallWideContent}>
              <WideVideoAlbumItem
                size='sm'
                id={slot.items[1].id}
                poster={slot.items[1].poster}
                name={slot.items[1].name}
                title={slot.items[1].name}
                description={slot.items[1].description}
                others={slot.items[1].others}
              />
            </Box>
            <Box className={classes.smallWideContent}>
              <WideVideoAlbumItem
                size='sm'
                id={slot.items[2].id}
                poster={slot.items[2].poster}
                name={slot.items[2].name}
                title={slot.items[2].name}
                description={slot.items[2].description}
                others={slot.items[2].others}
              />
            </Box>
          </Box>
        </Box>
        <Box className={classes.widePosterChild}>
          <Box className={classes.smallWideContainer}>
            <Box className={classes.smallWideContent}>
              <WideVideoAlbumItem
                size='sm'
                id={slot.items[3].id}
                poster={slot.items[3].poster}
                name={slot.items[3].name}
                title={slot.items[3].name}
                description={slot.items[3].description}
                others={slot.items[3].others}
              />
            </Box>
            <Box className={classes.smallWideContent}>
              <WideVideoAlbumItem
                size='sm'
                id={slot.items[4].id}
                poster={slot.items[4].poster}
                name={slot.items[4].name}
                title={slot.items[4].name}
                description={slot.items[4].description}
                others={slot.items[4].others}
              />
            </Box>
          </Box>
          <Box className={classes.largeWideContainer} mt={0.5}>
            <WideVideoAlbumItem
              size='lg'
              id={slot.items[5].id}
              poster={slot.items[5].poster}
              name={slot.items[5].name}
              title={slot.items[5].name}
              description={slot.items[5].description}
              others={slot.items[5].others}
            />
          </Box>
        </Box>
      </Box>
    </>
  );

  const SeedLayout = () => (
    <>
      {slot.items.map(movie => (
        <Grid item xs={6} md={3} key={movie.id}>
          <a href={isEmpty(movie.link) ? undefined : movie.link}>
            <img
              src={`${staticUrl}/${movie.poster.replace(/^\//, "")}`}
              alt={movie.name}
              className={classNames(
                classes.seedOverride,
                classes.smallWidePoster,
                classes.lightBackground,
              )}
            ></img>
          </a>
        </Grid>
      ))}
    </>
  );

  const renderIcon = () => {
    return slot.title ? (
      <CameraIcon
        display={hideTitleIcon || slot.icon === "" ? "none" : "inherit"}
        style={{ marginRight: 8 }}
      />
    ) : (
      ""
    );
  };

  const renderTitle = () => {
    return hideTitle ? (
      ""
    ) : (
      <>
        <Box className={classes.title}>
          {slot && renderIcon()}
          <Typography variant='h2'>{slot && slot.title}</Typography>
        </Box>
      </>
    );
  };

  const renderList = slot => {
    if (isEmpty(slot) || isEmpty(slot.items)) {
      return;
    }
    if (slot.layout === "img_topic") {
      return <ImgTopicLayout />;
    } else if (slot.layout === "seed") {
      return <SeedLayout />;
    } else if (slot.layout === "wide") {
      return slot.items.length >= 6 && <WidePosterLayout />;
    }
    return <DefaultLayout />;
  };

  return (
    <div>
      <Grid
        container
        direction='row'
        justify='space-between'
        alignItems='flex-end'
        spacing={1}
      >
        <Grid item>{renderTitle()}</Grid>
        <Grid item>
          <Box display={hideMoreLink || isEmpty(slot.more_link) ? "none" : ""}>
            <Button className={classes.moreButton} href={slot.more_link}>
              看全部
            </Button>
          </Box>
        </Grid>
      </Grid>
      <div className={classes.movieList}>
        <Grid container direction='row' alignItems='flex-end' sapcing={1}>
          {renderList(slot)}
        </Grid>
      </div>
    </div>
  );
};

MovieAlbum.defaultProps = {
  hideTitle: false,
  hideTitleIcon: false,
  hideMoreLink: false,
  hoverEnlargePoster: false,
  extraMarginBottom: false,
};

MovieAlbum.propsTypes = {
  slot: PropTypes.array.isRequired,
  hideTitle: PropTypes.bool,
  hideTitleIcon: PropTypes.bool,
  hideMoreLink: PropTypes.bool,
  hoverMaskMode: PropTypes.bool,
  hoverEnlargePoster: PropTypes.bool,
  extraMarginBottom: PropTypes.bool,
};

export default MovieAlbum;
